// frontend/src/pages/OperationsPage.tsx
// OPS-UX-02 — Composición visual de Operaciones, reutilizando los contratos existentes.

import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import PageHeader, { pageFrameSx } from '../modules/pricingJourney/components/PageHeader';

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import StorageRoundedIcon from '@mui/icons-material/StorageRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';

import {
  getCostCoverage,
  getDataProfile,
  getDataStatus,
  getQualityRules,
  getTransactionCount,
  getTransactionDateRange,
} from '../services/dataService';
import { listConnections } from '../services/connectionsService';
import { getSystemPulse } from '../services/optimizationService';
import { getMarginCenterCapabilities } from '../modules/marginCenter/services/marginCenterService';
import { useAuth } from '../context/AuthContext';
import type { ProcessingStatus } from '../types/processingStatus';
import type { DataProfileResponse, QualityRuleItem } from '../types';
import DataUploadPage from './DataUploadPage';
import PageSkeleton from '../modules/pricingJourney/components/Skeletons';
import { pricingNumberFormat } from '../modules/pricingJourney/formatNumber';

type Tone = 'success' | 'warning' | 'error' | 'neutral';
type SourceKind = 'customers' | 'products' | 'transactions';

const DATA_TYPES: readonly SourceKind[] = ['customers', 'products', 'transactions'];

const TONES: Record<Tone, { color: string; fill: string; border: string }> = {
  success: { color: '#6ee7b7', fill: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.27)' },
  warning: { color: '#fcd34d', fill: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.27)' },
  error: { color: '#fda4af', fill: 'rgba(251,113,133,0.10)', border: 'rgba(251,113,133,0.27)' },
  neutral: { color: '#9cc9eb', fill: 'rgba(56,189,248,0.08)', border: 'rgba(56,189,248,0.21)' },
};

const surfaceSx = {
  borderRadius: 3,
  border: '1px solid #293950',
  bgcolor: '#131e2e',
  backgroundImage: 'none',
  boxShadow: '0 16px 36px rgba(0, 5, 17, 0.12)',
};

const subdued = '#8ea5be';
const bright = '#f4f8ff';
const blue = '#7dd3fc';
const stroke = 'rgba(148,163,184,0.16)';

const formatNumber = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : pricingNumberFormat({ maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : `${pricingNumberFormat({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)} %`;

const formatFinancialPercent = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const truncated = Math.trunc(value * 100) / 100;
  return `${pricingNumberFormat({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(truncated)} %`;
};

const formatDate = (value: string | null | undefined, includeTime = false): string => {
  if (!value) return 'Sin registro';
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return 'Fecha no disponible';
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
    // PRC-041: las horas se muestran siempre en hora de Madrid.
    ...(includeTime ? { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' } : {}),
  }).format(parsed);
};

const latestDate = (values: Array<string | null | undefined>): string | null => {
  const dates = values
    .filter((item): item is string => Boolean(item))
    .map((item) => new Date(item).getTime())
    .filter((item) => Number.isFinite(item));
  return dates.length ? new Date(Math.max(...dates)).toISOString() : null;
};

const StatusChip: React.FC<{ label: string; tone: Tone }> = ({ label, tone }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      flexShrink: 0,
      height: 24,
      borderRadius: 1.25,
      fontSize: 11,
      fontWeight: 800,
      bgcolor: TONES[tone].fill,
      color: TONES[tone].color,
      border: `1px solid ${TONES[tone].border}`,
    }}
  />
);

const sourceState = (
  status: ProcessingStatus | null,
  isLoading: boolean,
  isError: boolean,
): { label: string; tone: Tone } => {
  if (isLoading) return { label: 'Consultando', tone: 'neutral' };
  if (isError) return { label: 'No disponible', tone: 'error' };
  if (!status) return { label: 'Sin carga', tone: 'neutral' };
  if (status.status === 'success') return { label: 'Procesado', tone: 'success' };
  if (status.status === 'processing') return { label: 'En proceso', tone: 'warning' };
  if (status.status === 'error') return { label: 'Revisar carga', tone: 'error' };
  return { label: 'Sin estado', tone: 'neutral' };
};

const moduleState = (status: string | null | undefined): { label: string; tone: Tone } => {
  if (status === 'success') return { label: 'Completado', tone: 'success' };
  if (status === 'running') return { label: 'En ejecución', tone: 'warning' };
  if (status === 'failed' || status === 'error') return { label: 'Incidencia', tone: 'error' };
  if (status === 'pending') return { label: 'Pendiente', tone: 'neutral' };
  return { label: 'Sin estado', tone: 'neutral' };
};

const IconSquare: React.FC<{ icon: React.ReactNode; color?: string; size?: number }> = ({
  icon, color = blue, size = 42,
}) => (
  <Box sx={{
    width: size, height: size, flexShrink: 0, borderRadius: 2,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color, bgcolor: `${color}13`, border: `1px solid ${color}24`,
  }}>
    {icon}
  </Box>
);

interface InventoryTileProps {
  name: string;
  caption: string;
  icon: React.ReactNode;
  color: string;
  status: ProcessingStatus | null;
  loading: boolean;
  error: boolean;
  onOpen: () => void;
}

const InventoryTile: React.FC<InventoryTileProps> = ({
  name, caption, icon, color, status, loading, error, onOpen,
}) => {
  const state = sourceState(status, loading, error);
  const count = status?.status === 'success' ? status.rows_processed : null;
  const recorded = status?.finished_at || status?.started_at;
  return (
    <Box sx={{
      borderRadius: 2.1, border: '1px solid #304054', bgcolor: '#101a29',
      px: { xs: 1.65, md: 1.9 }, py: 1.65, minWidth: 0,
      display: 'flex', flexDirection: 'column', gap: 1.15,
      transition: 'border-color 0.18s, background-color 0.18s',
      '&:hover': { borderColor: '#42607b', bgcolor: '#122033' },
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.65}>
        <Stack direction="row" alignItems="center" spacing={0.9} sx={{ minWidth: 0 }}>
          <IconSquare icon={icon} color={color} size={31} />
          <Typography sx={{ color: bright, fontWeight: 850, fontSize: 13.5 }}>{name}</Typography>
        </Stack>
        <StatusChip label={state.label} tone={state.tone} />
      </Stack>
      <Box>
        <Typography sx={{ fontSize: { xs: 28, md: 32 }, fontWeight: 900, color: bright, letterSpacing: '-0.045em', lineHeight: 1.04, fontVariantNumeric: 'tabular-nums' }}>
          {loading ? '…' : formatNumber(count)}
        </Typography>
        <Typography variant="caption" sx={{ color: subdued, fontSize: 11.5 }}>{caption}</Typography>
      </Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={0.5} sx={{ mt: 'auto' }}>
        <Typography variant="caption" sx={{ color: subdued, fontSize: 11.5 }}>
          {status ? `${status.status === 'success' ? 'Última carga' : 'Último intento'} · ${formatDate(recorded, true)}` : 'Sin carga registrada'}
        </Typography>
        <Button size="small" onClick={onOpen} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />}
          sx={{ color, fontWeight: 800, textTransform: 'none', px: 0, py: 0, minWidth: 0, fontSize: 11.5 }}>
          Actualizar
        </Button>
      </Stack>
    </Box>
  );
};

const MODULES = [
  { key: 'DATA_INGEST', title: 'Datos', icon: <StorageRoundedIcon fontSize="small" /> },
  { key: 'PRICING_ENGINE', title: 'Motor de precios', icon: <PsychologyRoundedIcon fontSize="small" /> },
  // PRC-039: sin tarjeta «Previsión» (Planificación retirada hasta Autonomy).
] as const;

const OperationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [showSchedule, setShowSchedule] = useState(false);
  const [showQuality, setShowQuality] = useState(false);
  const [activeUploadSource, setActiveUploadSource] = useState<SourceKind | null>(null);
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState<HTMLElement | null>(null);
  const { user, isLoading: isAuthLoading } = useAuth();
  const isProfileLoading = Boolean(user && !user.tier_details);
  const canAccessConnections = user?.tier_details?.feature_api_access === true;

  const dataStatusQueries = useQueries({
    queries: DATA_TYPES.map((kind) => ({
      queryKey: ['operations', 'data-status', kind],
      queryFn: () => getDataStatus(kind),
      staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1,
    })),
  });

  // Perfiles y reglas: únicamente se consultan cuando se abre Calidad.
  // Sus snapshots son anteriores a la aplicación de reglas; nunca se anuncian como calidad final.
  const qualityQueries = useQueries({
    queries: DATA_TYPES.flatMap((kind) => [
      {
        queryKey: ['operations', 'quality-profile', kind],
        queryFn: () => getDataProfile(kind),
        enabled: showQuality,
        staleTime: 60 * 1000,
        retry: 1,
      },
      {
        queryKey: ['operations', 'quality-rules', kind],
        queryFn: () => getQualityRules(kind),
        enabled: showQuality,
        staleTime: 60 * 1000,
        retry: 1,
      },
    ]),
  });

  const transactionCountQuery = useQuery({
    queryKey: ['operations', 'transaction-count'], queryFn: getTransactionCount,
    staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1,
  });
  const transactionRangeQuery = useQuery({
    queryKey: ['operations', 'transaction-range'], queryFn: getTransactionDateRange,
    staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1,
  });
  // Cobertura financiera de las transacciones: contrato canónico del Centro de Decisión.
  const financialCapabilitiesQuery = useQuery({
    queryKey: ['operations', 'financial-capabilities'], queryFn: getMarginCenterCapabilities,
    staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1,
  });
  // Cobertura de la tabla de costes históricos: una magnitud distinta y secundaria.
  const costCoverageQuery = useQuery({
    queryKey: ['operations', 'cost-coverage', 'monthly'], queryFn: () => getCostCoverage('monthly'),
    staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1,
  });
  const connectionsQuery = useQuery({
    queryKey: ['operations', 'connections'], queryFn: listConnections,
    enabled: canAccessConnections,
    staleTime: 60 * 1000, refetchOnWindowFocus: false, retry: 1,
  });
  const systemPulseQuery = useQuery({
    queryKey: ['operations', 'system-pulse'], queryFn: getSystemPulse,
    staleTime: 30 * 1000, refetchOnWindowFocus: false, retry: 1,
  });

  const latestCoverage = useMemo(() => {
    const rows = costCoverageQuery.data;
    if (!rows?.length) return null;
    return [...rows].sort((a, b) => a.period_start.localeCompare(b.period_start))[rows.length - 1] ?? null;
  }, [costCoverageQuery.data]);

  const statuses = dataStatusQueries.map((query) => query.data ?? null);
  const loadingSources = dataStatusQueries.some((query) => query.isLoading);
  const erroredSources = dataStatusQueries.some((query) => query.isError || query.data?.status === 'error');
  const completedSources = statuses.filter((status) => status?.status === 'success').length;
  const transactionStatus = statuses[2] ?? null;
  const transactionState = sourceState(transactionStatus, dataStatusQueries[2]?.isLoading ?? false, dataStatusQueries[2]?.isError ?? false);
  const transactionRecorded = transactionStatus?.finished_at || transactionStatus?.started_at;
  const transactionRange = transactionRangeQuery.data;

  const connections = connectionsQuery.data ?? [];
  const activeConnections = connections.filter((item) => item.is_active);
  const latestSync = latestDate(connections.map((item) => item.last_sync_at));

  const pulse = systemPulseQuery.data;
  const engine = pulse?.modules.PRICING_ENGINE;
  const tier = pulse?.tier;
  const isLocked = (key: string) =>
    tier === 'Discovery' && (key === 'DEMAND_FORECAST' || key === 'FUTURE_SIMULATION');
  const availableModules = MODULES.filter((item) => !isLocked(item.key));
  const nextCycleMs = availableModules
    .map((item) => pulse?.modules[item.key]?.next_scheduled_run)
    .filter((date): date is string => Boolean(date))
    .map((date) => new Date(date).getTime())
    .filter((date) => Number.isFinite(date))
    .sort((a, b) => a - b)[0];
  const hasModelError = availableModules.some((item) => {
    const status = pulse?.modules[item.key]?.status;
    return status === 'error' || status === 'failed';
  });

  let modelLabel = 'Consultando estado';
  let modelDescription = 'Estamos recuperando las últimas ejecuciones registradas.';
  let modelTone: Tone = 'neutral';
  if (systemPulseQuery.isError) {
    modelLabel = 'Estado no disponible';
    modelDescription = 'No se ha podido recuperar la información de los modelos.';
    modelTone = 'error';
  } else if (pulse?.global_status === 'processing') {
    modelLabel = 'Actualización en curso';
    modelDescription = 'Hay un proceso de datos o modelos en ejecución.';
    modelTone = 'warning';
  } else if (hasModelError) {
    modelLabel = 'Revisión necesaria';
    modelDescription = 'Al menos una última ejecución ha registrado una incidencia.';
    modelTone = 'error';
  } else if (pulse?.smart_action.reason === 'NO_DATA') {
    modelLabel = 'Pendiente de datos';
    modelDescription = 'Carga información comercial para preparar los modelos.';
  } else if (pulse?.smart_action.reason === 'COLD_START') {
    modelLabel = 'Preparación inicial pendiente';
    modelDescription = 'Hay datos disponibles, pero todavía no consta un entrenamiento inicial.';
    modelTone = 'warning';
  } else if (pulse?.smart_action.reason === 'NEW_DATA_AVAILABLE') {
    modelLabel = 'Nuevos datos disponibles';
    modelDescription = 'La última carga es posterior a la última ejecución del motor.';
    modelTone = 'warning';
  } else if (engine?.status === 'success') {
    modelLabel = 'Motor de Pricing disponible';
    modelDescription = 'Su última ejecución registrada finalizó correctamente.';
    modelTone = 'success';
  }

  const financialDataset = financialCapabilitiesQuery.data?.dataset;
  const hasFinancialActivity = typeof financialDataset?.transaction_rows === 'number' && financialDataset.transaction_rows > 0;
  const financialCoverage = hasFinancialActivity && typeof financialDataset?.cost_coverage_pct === 'number'
    && Number.isFinite(financialDataset.cost_coverage_pct)
    ? financialDataset.cost_coverage_pct : null;
  const unresolvedFinancialRows = hasFinancialActivity && typeof financialDataset?.unresolved_cost_rows === 'number'
    ? financialDataset.unresolved_cost_rows : null;
  const preciseFinancialCoverage = hasFinancialActivity
    && unresolvedFinancialRows !== null
    && typeof financialDataset?.transaction_rows === 'number'
    && financialDataset.transaction_rows > 0
      ? ((financialDataset.transaction_rows - unresolvedFinancialRows) / financialDataset.transaction_rows) * 100
      : financialCoverage;
  const coverageBar = preciseFinancialCoverage === null ? 0 : Math.min(100, Math.max(0, preciseFinancialCoverage));
  const financialCostState = financialCapabilitiesQuery.isError ? { label: 'No disponible', tone: 'error' as Tone }
    : financialCapabilitiesQuery.isLoading ? { label: 'Consultando', tone: 'neutral' as Tone }
      : !hasFinancialActivity ? { label: 'Sin actividad', tone: 'neutral' as Tone }
        : preciseFinancialCoverage === null ? { label: 'No calculable', tone: 'neutral' as Tone }
          : (unresolvedFinancialRows !== null && unresolvedFinancialRows > 0) || preciseFinancialCoverage < 100
            ? { label: 'Cobertura parcial', tone: 'warning' as Tone }
            : { label: 'Cobertura completa', tone: 'success' as Tone };

  // PRC-031: estructura al instante en lugar de una rueda.
  if (isAuthLoading || isProfileLoading) {
    return <PageSkeleton eyebrow="Operaciones" title="Operaciones" variant="cards" />;
  }

  return (
    <Box sx={pageFrameSx}>
      <Stack spacing={2.5}>
        {/* PRC-032: cabecera común; «Volver» solo con origen. */}
        <PageHeader
          eyebrow="Operaciones"
          title="Operaciones"
          description="Tus fuentes comerciales, modelos y herramientas de gestión en un mismo lugar."
          back="origin"
          sx={{ mb: 0 }}
        />

        {/* OPS-UX-03A — Tres fuentes en una sección compacta; el circuito de carga se conserva. */}
        <Paper elevation={0} sx={{ ...surfaceSx, overflow: 'hidden' }}>
          <Box sx={{ height: 2, bgcolor: '#38bdf8' }} />
          <Stack spacing={1.6} sx={{ p: { xs: 1.8, md: 2.2 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" gap={1.2}>
              <Stack direction="row" spacing={1.1} alignItems="center">
                <IconSquare icon={<StorageRoundedIcon fontSize="small" />} size={36} />
                <Box>
                  <Typography sx={{ color: bright, fontSize: 18, fontWeight: 900 }}>Datos comerciales</Typography>
                  <Typography variant="caption" sx={{ color: subdued }}>Volumen, periodo y última actualización de cada fuente.</Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <StatusChip
                  label={loadingSources ? 'Consultando fuentes' : erroredSources ? 'Hay cargas por revisar' : `${completedSources}/3 últimos procesos correctos`}
                  tone={erroredSources ? 'warning' : completedSources === 3 && !loadingSources ? 'success' : 'neutral'}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={(event) => setUploadMenuAnchor(event.currentTarget)}
                  startIcon={<CloudUploadRoundedIcon fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 850, borderRadius: 1.8, px: 1.65, bgcolor: '#38bdf8', color: '#061725', boxShadow: 'none', '&:hover': { bgcolor: '#7dd3fc', boxShadow: 'none' } }}
                >
                  Cargar datos
                </Button>
              </Stack>
            </Stack>

            <Menu
              anchorEl={uploadMenuAnchor}
              open={Boolean(uploadMenuAnchor)}
              onClose={() => setUploadMenuAnchor(null)}
              PaperProps={{ sx: { bgcolor: '#142236', color: bright, border: `1px solid ${stroke}`, borderRadius: 2 } }}
            >
              {([
                ['transactions', 'Transacciones / ventas'],
                ['customers', 'Clientes'],
                ['products', 'Productos'],
              ] as const).map(([source, label]) => (
                <MenuItem key={source} onClick={() => { setUploadMenuAnchor(null); setActiveUploadSource(source); }}>
                  {label}
                </MenuItem>
              ))}
            </Menu>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.35, alignItems: 'stretch' }}>
              <Box sx={{
                borderRadius: 2.1, px: { xs: 1.65, md: 1.9 }, py: 1.65, minWidth: 0,
                border: '1px solid #315873',
                background: 'linear-gradient(125deg, #142c40 0%, #101c2b 90%)',
                display: 'flex', flexDirection: 'column', gap: 1.15,
              }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.65}>
                  <Stack direction="row" spacing={0.9} alignItems="center">
                    <IconSquare icon={<ReceiptLongRoundedIcon fontSize="small" />} color="#7dd3fc" size={31} />
                    <Typography sx={{ color: bright, fontWeight: 850, fontSize: 13.5 }}>Transacciones</Typography>
                  </Stack>
                  <StatusChip label={transactionState.label} tone={transactionState.tone} />
                </Stack>
                <Box>
                  <Typography sx={{ color: bright, fontSize: { xs: 28, md: 32 }, fontWeight: 950, lineHeight: 1.04, letterSpacing: '-0.045em', fontVariantNumeric: 'tabular-nums' }}>
                    {transactionCountQuery.isLoading ? '…' : transactionCountQuery.isError ? '—' : formatNumber(transactionCountQuery.data?.total_count)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#a6bfd8', fontSize: 11.5 }}>Transacciones disponibles</Typography>
                  <Typography variant="caption" sx={{ color: '#b2d7f0', display: 'block', mt: 0.55, fontSize: 11.5 }}>
                    {transactionRangeQuery.isLoading
                      ? 'Consultando periodo…'
                      : transactionRangeQuery.isError
                        ? 'Periodo no disponible'
                        : transactionRange?.min_date && transactionRange?.max_date
                          ? `${formatDate(transactionRange.min_date)} — ${formatDate(transactionRange.max_date)}`
                          : 'Sin periodo registrado'}
                  </Typography>
                </Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={0.5} sx={{ mt: 'auto' }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#a6bfd8', display: 'block', fontSize: 11.5 }}>
                      {transactionStatus ? `${transactionStatus.status === 'success' ? 'Última carga' : 'Último intento'} · ${formatDate(transactionRecorded, true)}` : 'Sin carga registrada'}
                    </Typography>
                    {transactionStatus?.status === 'success' && transactionStatus.rows_processed != null && (
                      <Typography variant="caption" sx={{ color: '#7297b7', display: 'block', fontSize: 11 }}>
                        Último archivo · {formatNumber(transactionStatus.rows_processed)} registros procesados
                      </Typography>
                    )}
                  </Box>
                  <Button size="small" onClick={() => setActiveUploadSource('transactions')} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />}
                    sx={{ color: '#7dd3fc', fontWeight: 850, textTransform: 'none', px: 0, py: 0, minWidth: 0, fontSize: 11.5 }}>
                    Actualizar
                  </Button>
                </Stack>
              </Box>

              <InventoryTile
                name="Clientes" caption="Registros de la última carga"
                icon={<PeopleAltRoundedIcon fontSize="small" />} color="#a5b4fc"
                status={statuses[0] ?? null} loading={dataStatusQueries[0]?.isLoading ?? false} error={dataStatusQueries[0]?.isError ?? false}
                onOpen={() => setActiveUploadSource('customers')}
              />
              <InventoryTile
                name="Productos" caption="Registros de la última carga"
                icon={<Inventory2RoundedIcon fontSize="small" />} color="#5eead4"
                status={statuses[1] ?? null} loading={dataStatusQueries[1]?.isLoading ?? false} error={dataStatusQueries[1]?.isError ?? false}
                onOpen={() => setActiveUploadSource('products')}
              />
            </Box>

            <Box sx={{ borderTop: `1px solid ${stroke}`, pt: 1.25 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={0.65}>
                <Stack direction="row" spacing={0.85} alignItems="center" sx={{ minWidth: 0 }}>
                  <VerifiedUserRoundedIcon sx={{ fontSize: 16, color: '#86efac', flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ color: bright, fontSize: 12.5, fontWeight: 800 }}>Calidad y normalización</Typography>
                    <Typography variant="caption" sx={{ color: subdued }}>
                      Consulta los perfiles y reglas de cada fuente cuando lo necesites.
                    </Typography>
                  </Box>
                </Stack>
                <Button size="small" onClick={() => setShowQuality((current) => !current)} aria-expanded={showQuality}
                  endIcon={<ExpandMoreRoundedIcon sx={{ fontSize: 16, transition: 'transform 0.18s', transform: showQuality ? 'rotate(180deg)' : 'none' }} />}
                  sx={{ textTransform: 'none', fontWeight: 800, color: blue, flexShrink: 0, px: 0, fontSize: 11.5 }}>
                  {showQuality ? 'Ocultar diagnóstico' : 'Consultar diagnóstico'}
                </Button>
              </Stack>
              <Collapse in={showQuality} timeout={200} unmountOnExit>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25, mt: 1.5 }}>
                  {DATA_TYPES.map((kind, index) => {
                    const profileQuery = qualityQueries[index * 2];
                    const rulesQuery = qualityQueries[index * 2 + 1];
                    const profile = profileQuery?.data as DataProfileResponse | undefined;
                    const rules = rulesQuery?.data as QualityRuleItem[] | undefined;
                    const columns = profile?.column_profiles
                      ? Object.entries(profile.column_profiles).filter(([name]) => !['id', 'user_id', 'created_at', 'updated_at'].includes(name))
                      : [];
                    const incomplete = columns
                      .filter(([, stats]) => stats && Number.isFinite(stats.null_count) && stats.null_count > 0)
                      .sort((a, b) => (b[1].null_percentage || 0) - (a[1].null_percentage || 0));
                    const name = kind === 'customers' ? 'Clientes' : kind === 'products' ? 'Productos' : 'Transacciones';
                    const latestStatus = statuses[index];
                    return (
                      <Box key={kind} sx={{ minWidth: 0, border: `1px solid ${stroke}`, borderRadius: 2, bgcolor: '#101a29', p: 1.7 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <Typography sx={{ color: bright, fontSize: 13, fontWeight: 850 }}>{name}</Typography>
                          <StatusChip label={sourceState(latestStatus, dataStatusQueries[index]?.isLoading ?? false, dataStatusQueries[index]?.isError ?? false).label}
                            tone={sourceState(latestStatus, dataStatusQueries[index]?.isLoading ?? false, dataStatusQueries[index]?.isError ?? false).tone} />
                        </Stack>
                        {profileQuery?.isPending ? (
                          <Typography variant="caption" sx={{ color: subdued, display: 'block', mt: 1.5 }}>Consultando perfil…</Typography>
                        ) : profileQuery?.isError || !profile || !profile.column_profiles ? (
                          <Typography variant="caption" sx={{ color: '#fcd34d', display: 'block', mt: 1.5 }}>
                            No hay perfil consultable para esta fuente.
                          </Typography>
                        ) : (
                          <Stack spacing={0.9} sx={{ mt: 1.35 }}>
                            <Typography variant="caption" sx={{ color: subdued }}>
                              Último perfil guardado · {formatNumber(profile.row_count)} filas · {formatNumber(columns.length)} campos analizados
                            </Typography>
                            <Stack direction="row" alignItems="baseline" spacing={0.85}>
                              <Typography sx={{ color: incomplete.length ? '#fcd34d' : bright, fontSize: 23, fontWeight: 900, lineHeight: 1 }}>
                                {formatNumber(incomplete.length)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: subdued }}>campos con valores ausentes</Typography>
                            </Stack>
                            {incomplete.length > 0 && (
                              <Typography variant="caption" sx={{ color: '#cbd5e1', display: 'block' }}>
                                {incomplete.slice(0, 2).map(([field, stats]) => `${field} (${formatPercent(stats.null_percentage)})`).join(' · ')}
                                {incomplete.length > 2 ? ` · +${incomplete.length - 2} más` : ''}
                              </Typography>
                            )}
                          </Stack>
                        )}
                        <Typography variant="caption" sx={{ color: rulesQuery?.isError ? '#fcd34d' : subdued, display: 'block', mt: 1.25 }}>
                          {rulesQuery?.isPending ? 'Consultando reglas…' : rulesQuery?.isError ? 'Reglas no disponibles' : `${formatNumber(Array.isArray(rules) ? rules.length : 0)} reglas guardadas`}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1} sx={{ pt: 1.2 }}>
                  <Typography variant="caption" sx={{ color: '#7891aa', maxWidth: 850 }}>
                    El perfil corresponde al último snapshot guardado antes de aplicar reglas. Los campos incompletos no son por sí solos errores ni representan la calidad final del dataset.
                  </Typography>
                  <Button size="small" onClick={() => navigate('/data-quality')} endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />}
                    sx={{ textTransform: 'none', fontWeight: 800, color: blue, flexShrink: 0, px: 0, fontSize: 11.5 }}>
                    Gestionar reglas y ver detalle
                  </Button>
                </Stack>
              </Collapse>
            </Box>
            <Typography variant="caption" sx={{ color: '#7891aa', fontSize: 10.75 }}>
              Clientes y Productos indican los registros procesados en la última carga correcta si el último proceso es satisfactorio; no equivalen necesariamente al inventario total.
            </Typography>
          </Stack>
        </Paper>

        {/* Los modelos recuperan el pipeline conectado y su planificación consultable. */}
        <Paper elevation={0} sx={{ ...surfaceSx, overflow: 'hidden' }}>
          <Stack spacing={2.1} sx={{ p: { xs: 2, md: 2.75 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1.5}>
              <Stack direction="row" alignItems="center" spacing={1.3}>
                <IconSquare icon={<PsychologyRoundedIcon fontSize="small" />} color="#a5b4fc" />
                <Box>
                  <Typography sx={{ color: bright, fontSize: 19, fontWeight: 900 }}>Modelos y actualizaciones</Typography>
                  <Typography variant="body2" sx={{ color: subdued }}>Últimas ejecuciones y calendario de procesamiento.</Typography>
                </Box>
              </Stack>
              <Button size="small" onClick={() => navigate('/retraining')} endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
                sx={{ color: blue, fontWeight: 850, textTransform: 'none' }}>
                Gestión avanzada
              </Button>
            </Stack>

            <Box sx={{
              bgcolor: TONES[modelTone].fill, border: `1px solid ${TONES[modelTone].border}`,
              borderRadius: 2, py: 1.5, px: 1.8,
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
            }}>
              <Box sx={{ height: 9, width: 9, borderRadius: '50%', flexShrink: 0, bgcolor: TONES[modelTone].color }} />
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography sx={{ color: bright, fontWeight: 850, fontSize: 14 }}>{modelLabel}</Typography>
                <Typography variant="caption" sx={{ color: '#a8bbd0' }}>{modelDescription}</Typography>
              </Box>
              {engine?.last_run_at && (
                <Typography variant="caption" sx={{ color: subdued }}>
                  Motor · {formatDate(engine.last_run_at, true)}
                </Typography>
              )}
            </Box>

            {systemPulseQuery.isError && (
              <Alert severity="warning">No ha sido posible consultar el estado de los modelos. Las fuentes comerciales siguen disponibles por separado.</Alert>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', md: 'repeat(2,minmax(0,1fr))' }, gap: { xs: 1.2, md: 1.6 } }}>
              {MODULES.map((item, index) => {
                const locked = isLocked(item.key);
                const module = pulse?.modules[item.key];
                const state = locked ? { label: 'No incluido', tone: 'neutral' as Tone } : moduleState(module?.status);
                return (
                  <Box key={item.key} sx={{ position: 'relative', minWidth: 0 }}>
                    <Box sx={{
                      height: '100%', minHeight: 112, p: 1.85, borderRadius: 2.5,
                      border: `1px solid ${locked ? '#2a3446' : TONES[state.tone].border}`,
                      bgcolor: locked ? '#101827' : state.tone === 'success' ? 'rgba(22,51,49,0.48)' : '#101a29',
                      opacity: locked ? 0.66 : 1,
                    }}>
                      <Stack direction="row" spacing={1.05} alignItems="center" sx={{ mb: 1.15 }}>
                        <IconSquare icon={item.icon} color={TONES[state.tone].color} size={36} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: bright, fontWeight: 800, fontSize: 13.5 }}>{item.title}</Typography>
                          <Typography sx={{ color: TONES[state.tone].color, fontWeight: 900, fontSize: 10.5, textTransform: 'uppercase' }}>
                            {state.label}
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography variant="caption" sx={{ color: subdued, display: 'block' }}>
                        {locked ? 'Capacidad no incluida en tu plan' : module?.last_run_at ? `Última ejecución · ${formatDate(module.last_run_at, true)}` : 'Sin ejecución registrada'}
                      </Typography>
                    </Box>
                    {index < MODULES.length - 1 && (
                      <Box sx={{
                        position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)', zIndex: 1,
                        color: '#5f7892', display: { xs: 'none', xl: 'flex' },
                      }}><ChevronRightRoundedIcon fontSize="small" /></Box>
                    )}
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ borderTop: `1px solid ${stroke}`, pt: 1.65 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ScheduleRoundedIcon sx={{ fontSize: 18, color: '#829bb5' }} />
                  <Typography variant="body2" sx={{ color: '#afc0d4' }}>
                    Próximo ciclo previsto: <Box component="span" sx={{ color: bright, fontWeight: 800 }}>
                      {nextCycleMs !== undefined ? formatDate(new Date(nextCycleMs).toISOString(), true) : 'Sin fecha indicada'}
                    </Box>
                  </Typography>
                </Stack>
                <Button
                  onClick={() => setShowSchedule((current) => !current)}
                  aria-expanded={showSchedule}
                  endIcon={<ExpandMoreRoundedIcon sx={{ transition: 'transform 0.18s', transform: showSchedule ? 'rotate(180deg)' : 'none' }} />}
                  sx={{ color: blue, textTransform: 'none', fontWeight: 850, px: 0 }}
                >
                  {showSchedule ? 'Ocultar programación' : 'Ver programación de ciclos'}
                </Button>
              </Stack>
              <Collapse in={showSchedule} timeout={200}>
                <TableContainer sx={{ mt: 1.6, borderRadius: 2, border: `1px solid ${stroke}`, bgcolor: '#0f1928', overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 620, '& .MuiTableCell-root': { borderBottomColor: stroke, py: 1.65 } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#17253a' }}>
                        {['MÓDULO', 'ESTADO', 'ÚLTIMA EJECUCIÓN', 'PRÓXIMO CICLO (HORA DE MADRID)'].map((header) => (
                          <TableCell key={header} sx={{ fontSize: 11, fontWeight: 850, color: '#a2bad2' }}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {MODULES.map((item) => {
                        const locked = isLocked(item.key);
                        const module = pulse?.modules[item.key];
                        const state = locked ? { label: 'No incluido', tone: 'neutral' as Tone } : moduleState(module?.status);
                        return (
                          <TableRow key={item.key}>
                            <TableCell sx={{ color: bright, fontWeight: 800, fontSize: 12.5 }}>{item.title}</TableCell>
                            <TableCell><StatusChip label={state.label} tone={state.tone} /></TableCell>
                            <TableCell sx={{ color: subdued, fontSize: 12.5 }}>{locked ? '—' : formatDate(module?.last_run_at, true)}</TableCell>
                            <TableCell sx={{ color: '#acd4ef', fontSize: 12.5, fontWeight: 750 }}>
                              {locked ? '—' : module?.next_scheduled_run ? formatDate(module.next_scheduled_run, true) : 'Sin programación indicada'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant="caption" sx={{ color: '#7f97b1', mt: 1.1, display: 'block' }}>
                  Las fechas programadas son previsiones, no confirmaciones de ejecución.
                </Typography>
              </Collapse>
            </Box>
          </Stack>
        </Paper>

        {/* Información secundaria con sus propios indicadores, no tarjetas de navegación vacías. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,minmax(0,1fr))' }, gap: 2 }}>
          <Paper elevation={0} sx={{ ...surfaceSx, p: { xs: 2.2, md: 2.6 }, minWidth: 0 }}>
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1.1}>
                  <IconSquare icon={<HubRoundedIcon fontSize="small" />} color="#93c5fd" size={39} />
                  <Box>
                    <Typography sx={{ color: bright, fontWeight: 900, fontSize: 16 }}>Conexiones</Typography>
                    <Typography variant="caption" sx={{ color: subdued }}>Fuentes externas vinculadas</Typography>
                  </Box>
                </Stack>
                <StatusChip
                  label={!canAccessConnections ? 'No incluido' : connectionsQuery.isError ? 'No disponible' : connectionsQuery.isLoading ? 'Consultando' : connections.length === 0 ? 'Sin configurar' : `${activeConnections.length} activas`}
                  tone={!canAccessConnections ? 'neutral' : connectionsQuery.isError ? 'error' : activeConnections.length ? 'success' : 'neutral'}
                />
              </Stack>
              {!canAccessConnections ? (
                <Typography variant="body2" sx={{ color: subdued, py: 1 }}>
                  Las conexiones externas no están incluidas en las capacidades de tu plan. La carga manual de datos continúa disponible.
                </Typography>
              ) : connectionsQuery.isLoading ? (
                <Typography variant="body2" sx={{ color: subdued, py: 1 }}>Consultando las conexiones configuradas…</Typography>
              ) : connectionsQuery.isError ? (
                <Typography variant="body2" sx={{ color: '#fda4af', py: 1 }}>No hemos podido recuperar el estado de las conexiones.</Typography>
              ) : connections.length === 0 ? (
                <Box sx={{ flex: 1, py: 0.5 }}>
                  <Typography sx={{ color: bright, fontWeight: 850, fontSize: 17 }}>Todavía no hay conexiones externas</Typography>
                  <Typography variant="body2" sx={{ color: subdued, mt: 0.6 }}>
                    Puedes continuar cargando archivos manualmente o configurar una integración cuando la necesites.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Stack direction="row" alignItems="baseline" spacing={1.25}>
                    <Typography sx={{ color: bright, fontSize: 36, fontWeight: 950, lineHeight: 1, letterSpacing: '-0.04em' }}>
                      {formatNumber(activeConnections.length)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: subdued }}>
                      activas de {connections.length} configuradas
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: subdued }}>
                    {latestSync ? `Última sincronización registrada · ${formatDate(latestSync, true)}` : 'Todavía no hay sincronizaciones registradas'}
                  </Typography>
                </>
              )}
              {canAccessConnections && (
                <Box sx={{ borderTop: `1px solid ${stroke}`, pt: 1.2, mt: 'auto' }}>
                  <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <Button size="small" onClick={() => navigate('/connections')} endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
                      sx={{ textTransform: 'none', fontWeight: 850, color: blue, p: 0 }}>
                      {connections.length === 0 && !connectionsQuery.isLoading && !connectionsQuery.isError ? 'Configurar conexión' : 'Gestionar conexiones'}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ ...surfaceSx, p: { xs: 2.2, md: 2.6 }, minWidth: 0 }}>
            <Stack spacing={1.5} sx={{ height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1.1}>
                  <IconSquare icon={<PaymentsRoundedIcon fontSize="small" />} color="#67e8f9" size={39} />
                  <Box>
                    <Typography sx={{ color: bright, fontWeight: 900, fontSize: 16 }}>Cobertura financiera</Typography>
                    <Typography variant="caption" sx={{ color: subdued }}>Histórico transaccional disponible · cobertura por filas</Typography>
                  </Box>
                </Stack>
                <StatusChip label={financialCostState.label} tone={financialCostState.tone} />
              </Stack>
              <Stack direction="row" alignItems="baseline" spacing={1.4}>
                <Typography sx={{ color: bright, fontWeight: 950, letterSpacing: '-0.045em', fontSize: 36, lineHeight: 1 }}>
                  {financialCapabilitiesQuery.isLoading ? '…' : formatFinancialPercent(preciseFinancialCoverage)}
                </Typography>
                <Typography variant="body2" sx={{ color: subdued }}>de transacciones con coste resuelto</Typography>
              </Stack>
              {financialCoverage !== null ? (
                <LinearProgress variant="determinate" value={coverageBar}
                  sx={{ height: 7, borderRadius: 99, bgcolor: '#24364b', '& .MuiLinearProgress-bar': { bgcolor: '#67e8f9', borderRadius: 99 } }} />
              ) : (
                <Typography variant="caption" sx={{ color: subdued }}>
                  {financialCapabilitiesQuery.isError ? 'No se ha podido consultar la cobertura financiera.' : 'Sin universo financiero evaluable.'}
                </Typography>
              )}
              {hasFinancialActivity && (
                <Typography variant="caption" sx={{ color: '#a6bfd8' }}>
                  {formatNumber(financialDataset?.transaction_rows)} transacciones · {formatNumber(unresolvedFinancialRows)} filas sin coste resuelto
                </Typography>
              )}
              <Box sx={{ borderTop: `1px solid ${stroke}`, pt: 1.1, mt: 'auto' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap gap={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: subdued, display: 'block' }}>Costes históricos · cobertura distinta</Typography>
                    <Typography variant="caption" sx={{ color: '#a6bfd8', display: 'block' }}>
                      {costCoverageQuery.isLoading ? 'Consultando periodo…'
                        : costCoverageQuery.isError ? 'No se ha podido consultar su cobertura.'
                          : latestCoverage
                            ? `${latestCoverage.period_label} · ${formatNumber(latestCoverage.products_with_cost)} de ${formatNumber(latestCoverage.distinct_products_sold)} productos vendidos con coste histórico`
                            : 'Sin periodo registrado'}
                    </Typography>
                  </Box>
                  <Button size="small" onClick={() => navigate('/historical-costs')} endIcon={<ArrowForwardRoundedIcon fontSize="small" />}
                    sx={{ textTransform: 'none', fontWeight: 850, color: blue, p: 0 }}>
                    Gestionar costes históricos
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Stack>
      {activeUploadSource && (
        <DataUploadPage
          key={activeUploadSource}
          modalSource={activeUploadSource}
          onModalClosed={() => setActiveUploadSource(null)}
        />
      )}
    </Box>
  );
};

export default OperationsPage;
